import type { WorkshopProject, ElectricalWire } from './types';
import {
  createPowerSource,
  createMcb3P,
  createMcb1P,
  createContactor3P,
  createThermalRelay,
  createStopButton,
  createStartButton,
  createPilotLamp,
  createMotor3Phase,
  createTopWireDuct,
  createMidWireDuct,
  createLeftWireDuct,
  createRightWireDuct,
} from './catalog';

/**
 * Sıfır Boş Pano: Kullanıcının sol panelden kanalları ve komponentleri sıfırdan yerleştirmesi için.
 */
export function createBlankPanelProject(): WorkshopProject {
  const pwr = createPowerSource('pwr', 60, 60);
  return {
    id: 'circuit_blank_panel',
    name: 'Sıfır Boş Pano (Kendi Panonu Tasarla)',
    devices: [pwr],
    wires: [],
  };
}

/**
 * Fabrika Şablonu: Doğrudan Başlatmalı ve Mühürlemeli 3 Fazlı Motor Kumanda Devresi
 */
export function createDefaultDolStarterProject(): WorkshopProject {
  const pwr = createPowerSource('pwr', 60, 60);
  const f1 = createMcb3P('f1', 'F1', 80, 200);
  const f2 = createMcb1P('f2', 'F2', 200, 200);
  const km1 = createContactor3P('km1', 'KM1', 280, 200);
  const fr1 = createThermalRelay('fr1', 'FR1', 280, 360);
  const sb1 = createStopButton('sb1', 'SB1', 480, 160);
  const sb2 = createStartButton('sb2', 'SB2', 480, 240);
  const hl1 = createPilotLamp('hl1', 'HL1', '#22c55e', 480, 320); // Yeşil Çalışıyor
  const hl2 = createPilotLamp('hl2', 'HL2', '#ef4444', 480, 390); // Kırmızı Arıza
  const m1 = createMotor3Phase('m1', 'M1', 280, 560);
  const ductTop = createTopWireDuct();
  const ductMid = createMidWireDuct();
  const ductLeft = createLeftWireDuct();
  const ductRight = createRightWireDuct();

  const wires: ElectricalWire[] = [
    // --- GÜÇ DEVRESİ (3 FAZ 400V~) ---
    // Şebekeden 3P Sigortaya
    { id: 'w_pwr_f1_l1', from: { deviceId: 'pwr', terminalId: 'L1' }, to: { deviceId: 'f1', terminalId: '1' }, color: '#78350f', sectionMm2: 2.5 },
    { id: 'w_pwr_f1_l2', from: { deviceId: 'pwr', terminalId: 'L2' }, to: { deviceId: 'f1', terminalId: '3' }, color: '#1f2937', sectionMm2: 2.5 },
    { id: 'w_pwr_f1_l3', from: { deviceId: 'pwr', terminalId: 'L3' }, to: { deviceId: 'f1', terminalId: '5' }, color: '#64748b', sectionMm2: 2.5 },

    // 3P Sigortadan Kontaktör Ana Kontaklarına
    { id: 'w_f1_km1_1', from: { deviceId: 'f1', terminalId: '2' }, to: { deviceId: 'km1', terminalId: '1' }, color: '#78350f', sectionMm2: 2.5 },
    { id: 'w_f1_km1_3', from: { deviceId: 'f1', terminalId: '4' }, to: { deviceId: 'km1', terminalId: '3' }, color: '#1f2937', sectionMm2: 2.5 },
    { id: 'w_f1_km1_5', from: { deviceId: 'f1', terminalId: '6' }, to: { deviceId: 'km1', terminalId: '5' }, color: '#64748b', sectionMm2: 2.5 },

    // Kontaktörden Termik Röleye
    { id: 'w_km1_fr1_1', from: { deviceId: 'km1', terminalId: '2' }, to: { deviceId: 'fr1', terminalId: '1' }, color: '#78350f', sectionMm2: 2.5 },
    { id: 'w_km1_fr1_3', from: { deviceId: 'km1', terminalId: '4' }, to: { deviceId: 'fr1', terminalId: '3' }, color: '#1f2937', sectionMm2: 2.5 },
    { id: 'w_km1_fr1_5', from: { deviceId: 'km1', terminalId: '6' }, to: { deviceId: 'fr1', terminalId: '5' }, color: '#64748b', sectionMm2: 2.5 },

    // Termik Röleden Motora
    { id: 'w_fr1_m1_u1', from: { deviceId: 'fr1', terminalId: '2' }, to: { deviceId: 'm1', terminalId: 'U1' }, color: '#78350f', sectionMm2: 2.5 },
    { id: 'w_fr1_m1_v1', from: { deviceId: 'fr1', terminalId: '4' }, to: { deviceId: 'm1', terminalId: 'V1' }, color: '#1f2937', sectionMm2: 2.5 },
    { id: 'w_fr1_m1_w1', from: { deviceId: 'fr1', terminalId: '6' }, to: { deviceId: 'm1', terminalId: 'W1' }, color: '#64748b', sectionMm2: 2.5 },

    // Motor Gövde Topraklaması (PE)
    { id: 'w_pwr_m1_pe', from: { deviceId: 'pwr', terminalId: 'PE' }, to: { deviceId: 'm1', terminalId: 'PE' }, color: '#84cc16', sectionMm2: 2.5 },

    // --- KUMANDA DEVRESİ (230V~) ---
    // Şebeke L1'den Kumanda Sigortasına (F2)
    { id: 'w_pwr_f2_1', from: { deviceId: 'pwr', terminalId: 'L1' }, to: { deviceId: 'f2', terminalId: '1' }, color: '#78350f', sectionMm2: 1.5 },

    // F2 çıkışından Termik Röle NC (95) klemensine
    { id: 'w_f2_fr1_95', from: { deviceId: 'f2', terminalId: '2' }, to: { deviceId: 'fr1', terminalId: '95' }, color: '#1f2937', sectionMm2: 1.5 },

    // Termik Röle NC (96) çıkışından Stop Butonu (1) girişine
    { id: 'w_fr1_sb1_1', from: { deviceId: 'fr1', terminalId: '96' }, to: { deviceId: 'sb1', terminalId: '1' }, color: '#1f2937', sectionMm2: 1.5 },

    // Stop Butonu (2) çıkışından Start Butonu (3) girişine
    { id: 'w_sb1_sb2_3', from: { deviceId: 'sb1', terminalId: '2' }, to: { deviceId: 'sb2', terminalId: '3' }, color: '#1f2937', sectionMm2: 1.5 },

    // Stop (2) çıkışından Kontaktör (13 NO) Mühürleme kontağına paralel köprü
    { id: 'w_sb1_km1_13', from: { deviceId: 'sb1', terminalId: '2' }, to: { deviceId: 'km1', terminalId: '13' }, color: '#1f2937', sectionMm2: 1.5 },

    // Start Butonu (4) çıkışından Kontaktör (A1) bobinine
    { id: 'w_sb2_km1_a1', from: { deviceId: 'sb2', terminalId: '4' }, to: { deviceId: 'km1', terminalId: 'A1' }, color: '#1f2937', sectionMm2: 1.5 },

    // Kontaktör (14 NO) çıkışından Kontaktör (A1) bobinine mühürleme köprüsü
    { id: 'w_km1_14_a1', from: { deviceId: 'km1', terminalId: '14' }, to: { deviceId: 'km1', terminalId: 'A1' }, color: '#1f2937', sectionMm2: 1.5 },

    // Bobin (A1)'den Yeşil Çalışma Lambası (X1)'e paralel bağlantı
    { id: 'w_km1_hl1_x1', from: { deviceId: 'km1', terminalId: 'A1' }, to: { deviceId: 'hl1', terminalId: 'X1' }, color: '#1f2937', sectionMm2: 1.5 },

    // Kontaktör Bobini (A2)'den Şebeke Nötr (N) hattına
    { id: 'w_km1_a2_n', from: { deviceId: 'km1', terminalId: 'A2' }, to: { deviceId: 'pwr', terminalId: 'N' }, color: '#0284c7', sectionMm2: 1.5 },

    // Yeşil Lamba (X2)'den Şebeke Nötr (N) hattına
    { id: 'w_hl1_x2_n', from: { deviceId: 'hl1', terminalId: 'X2' }, to: { deviceId: 'pwr', terminalId: 'N' }, color: '#0284c7', sectionMm2: 1.5 },

    // --- TERMİK ARIZA İHBAR HATTI ---
    // Kumanda sigortası çıkışından Termik NO (97) girişine besleme
    { id: 'w_f2_fr1_97', from: { deviceId: 'f2', terminalId: '2' }, to: { deviceId: 'fr1', terminalId: '97' }, color: '#1f2937', sectionMm2: 1.5 },

    // Termik NO (98) çıkışından Kırmızı Arıza Lambası (X1) girişine
    { id: 'w_fr1_hl2_x1', from: { deviceId: 'fr1', terminalId: '98' }, to: { deviceId: 'hl2', terminalId: 'X1' }, color: '#1f2937', sectionMm2: 1.5 },

    // Kırmızı Arıza Lambası (X2)'den Nötr (N) hattına
    { id: 'w_hl2_x2_n', from: { deviceId: 'hl2', terminalId: 'X2' }, to: { deviceId: 'pwr', terminalId: 'N' }, color: '#0284c7', sectionMm2: 1.5 },
  ];

  return {
    id: 'circuit_dol_starter',
    name: 'Mühürlemeli Doğrudan Motor Kumanda Devresi',
    devices: [pwr, f1, f2, km1, fr1, sb1, sb2, hl1, hl2, m1, ductTop, ductMid, ductLeft, ductRight],
    wires,
  };
}

/**
 * Eğitim Şablonu: Kesikli Çalışma (Jogging / İmpuls) Kumanda Devresi
 * Mühürleme kontağı (13-14) devrede yoktur; Start butonuna basıldığı sürece motor döner.
 */
export function createJoggingCircuitProject(): WorkshopProject {
  const dol = createDefaultDolStarterProject();
  // Mühürleme kablolarını (13 ve 14 köprüleri) devreden çıkar
  const wires = dol.wires.filter(
    (w) => w.id !== 'w_sb1_km1_13' && w.id !== 'w_km1_14_a1'
  );

  return {
    id: 'circuit_jogging',
    name: 'Kesikli Çalışma (Jogging) Kumanda Devresi',
    devices: dol.devices,
    wires,
  };
}

/**
 * Eğitim Şablonu: Boş Pano (Kablolama Uygulaması)
 * Tüm cihazlar panoya yerleşiktir fakat hiçbir kablo bağlı değildir.
 * Öğrencinin sıfırdan klemensleri bağlayarak devreyi kurması için tasarlanmıştır.
 */
export function createEmptyWorkshopProject(): WorkshopProject {
  const dol = createDefaultDolStarterProject();
  return {
    id: 'circuit_empty_panel',
    name: 'Boş Pano (Öğrenci Kablolama Uygulaması)',
    devices: dol.devices,
    wires: [],
  };
}

/**
 * Eğitim Şablonu: Arıza Arama ve Hata Bulma (Troubleshooting)
 * Devrede arıza senaryoları mevcuttur:
 * 1. Termik röle atık (trip) durumdadır (kırmızı arıza lambası yanar, kumanda devresi kesilir).
 * 2. Start (4) ile Kontaktör (A1) arasındaki kumanda kablosu kopuktur / eksiktir.
 * Görev: Multimetre probunu kullanarak potansiyelleri ölçün, termiği Reset'leyin ve eksik kabloyu bağlayın!
 */
export function createTroubleshootingFaultCircuitProject(): WorkshopProject {
  const dol = createDefaultDolStarterProject();
  
  // Termik röleyi tripped (arızalı) duruma getir
  const devices = dol.devices.map((d) => {
    if (d.id === 'fr1') {
      return { ...d, props: { ...d.props, tripped: true } };
    }
    return d;
  });

  // Start (4) -> KM1 (A1) kablosunu devreden çıkar (kopuk hat simülasyonu)
  const wires = dol.wires.filter((w) => w.id !== 'w_sb2_km1_a1');

  return {
    id: 'circuit_troubleshooting',
    name: 'Arıza Arama & Hata Bulma (Troubleshooting Görevi)',
    devices,
    wires,
  };
}

export interface PresetCircuitOption {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly factory: () => WorkshopProject;
}

export const PRESET_CIRCUITS: readonly PresetCircuitOption[] = [
  {
    id: 'blank',
    label: '0. Sıfır Boş Pano (Kendi Panonu Tasarla)',
    description: 'Pano içi boştur. Kablo kanallarını ve kumanda elemanlarını sol panelden seçerek yerleştirin.',
    factory: createBlankPanelProject,
  },
  {
    id: 'dol',
    label: '1. Kesintisiz Çalışma (DOL Mühürleme)',
    description: 'Start ile mühürlenen, Stop veya Termik ile duran klasik motor kumanda devresi.',
    factory: createDefaultDolStarterProject,
  },
  {
    id: 'jogging',
    label: '2. Kesikli Çalışma (Jogging / İmpuls)',
    description: 'Mühürlemesiz kumanda devresi: Butona basıldığı sürece motor çalışır.',
    factory: createJoggingCircuitProject,
  },
  {
    id: 'empty',
    label: '3. Hazır Montajlı Pano (Kablolama Görevi)',
    description: 'Cihazlar montajlı, tüm klemensler boşta. Sıfırdan kablolama alıştırması.',
    factory: createEmptyWorkshopProject,
  },
  {
    id: 'troubleshooting',
    label: '4. Arıza Arama Senaryosu (Hata Bulma)',
    description: 'Termik atmış ve start hattı kopuk. Multimetre probuyla arızayı bulun ve devreyi devreye alın.',
    factory: createTroubleshootingFaultCircuitProject,
  },
];


